import { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts'; // needed for echarts prop on ReactECharts
import { useApi, useMetaData } from '../hooks/useApi';
import { useMonth } from '../context/MonthContext';
import { Geo } from '../utils/api';
import { ChartCard, ChartLoader, ChartError, StackedBar, LineChart } from '../components/Charts';
import { ThemeContext } from '../context/ThemeContext';
import { useCategory } from '../context/CategoryContext';
import { geoName } from '../utils/postcodes';
import { PALETTE, COLORS, fmtNum } from '../utils/theme';
import { UK_MAP_NAME as MAP_NAME, POSTCODE_REGION, REGION_POSTCODES, loadUKMap } from '../utils/ukMap';

const fmtMonth = m => {
  if (!m) return m;
  const [y, mo] = m.split('-');
  return `${['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][+mo - 1]} ${y}`;
};

// ── Main Component ────────────────────────────────────────────────────────────
export default function LocationIntelligenceView() {
  const { dark } = useContext(ThemeContext);
  const { category } = useCategory();
  const meta = useMetaData();
  const { month: ctxMonth } = useMonth();

  const [sessionType,      setSessionType]      = useState('open_app');
  const [mapReady,         setMapReady]         = useState(false);
  const [geoError,         setGeoError]         = useState(false);
  const [selectedRegion,   setSelectedRegion]   = useState(null);
  const [selectedPostcode, setSelectedPostcode] = useState(null);

  const effectiveCat = category || 'ALL';
  const month        = ctxMonth || meta.latestMonth;
  const p            = useMemo(() => ({ category: effectiveCat, session_type: sessionType, month_id: month }), [effectiveCat, sessionType, month]);
  const drillEnabled = !!selectedPostcode && !!month;

  useEffect(() => { loadUKMap(ok => { setMapReady(ok); setGeoError(!ok); }); }, []);

  // All postcodes snapshot for the month (map coloring)
  const snapshot = useApi(Geo.snapshot, { ...p, limit: 0 }, !!month);

  // Drill-down: 6-month per-app breakdown + share trend for selected postcode
  const appsData  = useApi(Geo.postcodeAppsTrend, { ...p, geography: selectedPostcode || '' }, drillEnabled);
  const trendData = useApi(Geo.postcodeTrend,      { ...p, geography: selectedPostcode || '' }, drillEnabled);

  // Aggregate snapshot rows → region totals for map
  const { regionData, maxRegionVal, postcodesByUsers } = useMemo(() => {
    if (!snapshot.data) return { regionData: [], maxRegionVal: 1, postcodesByUsers: [] };
    const valid = snapshot.data.filter(r => r.geography && r.geography.toLowerCase() !== 'unknown');
    const regionTotals = {};
    valid.forEach(r => {
      const region = POSTCODE_REGION[r.geography];
      if (region) regionTotals[region] = (regionTotals[region] || 0) + (r.active_users || 0);
    });
    const regionData = Object.entries(regionTotals).map(([name, value]) => ({ name, value }));
    const maxRegionVal = Math.max(1, ...regionData.map(d => d.value));
    const postcodesByUsers = valid.sort((a, b) => b.active_users - a.active_users);
    return { regionData, maxRegionVal, postcodesByUsers };
  }, [snapshot.data]);

  // Postcode list filtered by selected region
  const filteredPostcodes = useMemo(() => {
    if (!selectedRegion) return postcodesByUsers;
    const regionCodes = new Set(REGION_POSTCODES[selectedRegion] || []);
    return postcodesByUsers.filter(r => regionCodes.has(r.geography));
  }, [postcodesByUsers, selectedRegion]);

  // ECharts map option
  const mapOption = useMemo(() => {
    if (!mapReady) return null;
    return {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'item',
        formatter: p => `<b>${p.name}</b><br/>Active users: <b>${fmtNum(p.value || 0)}</b>`,
        backgroundColor: dark ? '#1e2633' : '#fff',
        borderColor: dark ? '#2d3748' : '#E5E7EB',
        borderWidth: 1,
        textStyle: { color: dark ? '#F9FAFB' : '#111827', fontSize: 12 },
        extraCssText: 'box-shadow:0 4px 12px rgba(0,0,0,0.12);border-radius:8px;',
      },
      visualMap: {
        min: 0,
        max: maxRegionVal,
        text: ['High', 'Low'],
        realtime: false,
        calculable: true,
        inRange: { color: ['#E1F5EE', '#5DCAA5', '#1DB89A', '#0F6E56'] },
        textStyle: { color: dark ? '#9CA3AF' : '#6B7280', fontSize: 10 },
        right: 8,
        top: 'middle',
        itemWidth: 12,
        itemHeight: 100,
      },
      series: [{
        type: 'map',
        map: MAP_NAME,
        roam: true,
        data: regionData,
        selectedMode: 'single',
        emphasis: {
          label: { show: true, fontSize: 10, color: '#fff' },
          itemStyle: { areaColor: '#f59e0b' },
        },
        select: {
          label: { show: true, fontSize: 10, color: '#fff' },
          itemStyle: { areaColor: '#f59e0b' },
        },
        label: { show: false },
        itemStyle: {
          areaColor: dark ? '#1e2633' : '#E8ECF4',
          borderColor: dark ? '#374151' : '#fff',
          borderWidth: 0.8,
        },
      }],
    };
  }, [mapReady, regionData, maxRegionVal, dark]);

  const onMapClick = useCallback(e => {
    const region = e?.name;
    if (!region) return;
    setSelectedRegion(prev => prev === region ? null : region);
    setSelectedPostcode(null);
  }, []);

  // Pivot 6-month app trend: months → x-axis, apps → series
  const { appCategories, appSeries } = useMemo(() => {
    const rows = appsData.data || [];
    if (!rows.length) return { appCategories: [], appSeries: [] };

    // Collect sorted unique months and unique app names
    const monthSet = [...new Set(rows.map(r => r.month_id))].sort();
    const appNames = [...new Set(rows.map(r => r.application))];

    // Build lookup: month_id + app → active_users
    const lookup = {};
    rows.forEach(r => { lookup[`${r.month_id}|${r.application}`] = r.active_users || 0; });

    const appSeries = appNames.map((app, i) => ({
      name: app,
      color: PALETTE[i % PALETTE.length],
      data: monthSet.map(m => lookup[`${m}|${app}`] || 0),
    }));

    return { appCategories: monthSet.map(fmtMonth), appSeries };
  }, [appsData.data]);

  const trendMonths = useMemo(() => (trendData.data || []).map(r => fmtMonth(r.month_id)), [trendData.data]);
  const trendSeries = useMemo(() => {
    if (!trendData.data?.length) return [];
    return [{
      name: `${geoName(selectedPostcode)} share`,
      data: trendData.data.map(r => r.share_pct),
      color: COLORS.teal,
      area: true,
    }];
  }, [trendData.data, selectedPostcode]);

  const selectedPostcodeRow = snapshot.data?.find(r => r.geography === selectedPostcode);
  const hasDrillDown = !!selectedPostcode;

  return (
    <div>
      {/* ── Filter bar ─────────────────────────────────────────────── */}
      <div className="filters-bar" style={{ flexWrap: 'wrap' }}>
        <span className="filter-label">Session:</span>
        <select className="filter-select" value={sessionType} onChange={e => { setSessionType(e.target.value); setSelectedPostcode(null); }}>
          {(meta.sessionTypes?.length ? meta.sessionTypes : ['open_app', 'background']).map(s => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        {selectedRegion && (
          <button
            onClick={() => { setSelectedRegion(null); setSelectedPostcode(null); }}
            style={{ fontSize: 12, fontWeight: 600, color: '#EF4444', background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.20)', borderRadius: 100, padding: '4px 12px', cursor: 'pointer' }}
          >
            ✕ {selectedRegion}
          </button>
        )}
        {selectedPostcode && (
          <button
            onClick={() => setSelectedPostcode(null)}
            style={{ fontSize: 12, fontWeight: 600, color: '#f59e0b', background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: 100, padding: '4px 12px', cursor: 'pointer' }}
          >
            ✕ {geoName(selectedPostcode)} ({selectedPostcode})
          </button>
        )}
      </div>

      {/* ── Map + panel grid ────────────────────────────────────────── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: (selectedRegion || hasDrillDown) ? '1fr 1fr' : '1fr',
        gap: 16,
        alignItems: 'start',
      }}>

        {/* Choropleth map */}
        <ChartCard
          title="Active Users by Region"
          sub={`${effectiveCat} · ${fmtMonth(month)} · click a region to explore postcodes`}
        >
          {geoError && <ChartError message="Failed to load UK map data" height={520} />}
          {!geoError && (snapshot.loading || !mapReady) && <ChartLoader height={520} />}
          {!geoError && mapReady && !snapshot.loading && snapshot.error && <ChartError message={snapshot.error} height={520} />}
          {!geoError && mapReady && !snapshot.loading && !snapshot.error && mapOption && (
            regionData.length > 0 ? (
              <ReactECharts
                key={selectedRegion ? 'split' : 'full'}
                echarts={echarts}
                option={mapOption}
                style={{ height: 520 }}
                onEvents={{ click: onMapClick }}
                notMerge
              />
            ) : (
              <div className="empty-state" style={{ height: 520 }}>No data for selected filters</div>
            )
          )}
        </ChartCard>

        {/* Right panel: postcode list → drill-down charts */}
        {(selectedRegion || hasDrillDown) && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* Postcode list (always shown when a region is selected) */}
            {!hasDrillDown && (
              <ChartCard
                title={selectedRegion ? `Postcodes — ${selectedRegion}` : 'All Postcodes'}
                sub="Click a postcode to see app market share and trend"
              >
                {snapshot.loading ? <ChartLoader height={300} /> : (
                  <div style={{ maxHeight: 480, overflowY: 'auto' }}>
                    {filteredPostcodes.length === 0 && (
                      <div className="empty-state" style={{ height: 160 }}>No postcode data for this region</div>
                    )}
                    {filteredPostcodes.map((r, i) => {
                      const pct = Math.round((r.active_users / (filteredPostcodes[0]?.active_users || 1)) * 100);
                      return (
                        <div
                          key={r.geography}
                          onClick={() => setSelectedPostcode(r.geography)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 10,
                            padding: '8px 12px', cursor: 'pointer', borderRadius: 8,
                            background: r.geography === selectedPostcode ? 'rgba(29,184,154,0.10)' : 'transparent',
                            transition: 'background 0.15s',
                          }}
                          onMouseEnter={e => { if (r.geography !== selectedPostcode) e.currentTarget.style.background = 'var(--bg-card-hover, rgba(0,0,0,0.04))'; }}
                          onMouseLeave={e => { if (r.geography !== selectedPostcode) e.currentTarget.style.background = 'transparent'; }}
                        >
                          <span style={{ width: 20, fontSize: 11, color: 'var(--text-muted)', textAlign: 'right', flexShrink: 0 }}>{i + 1}</span>
                          <span style={{ width: 32, fontWeight: 700, fontSize: 12, color: COLORS.teal, flexShrink: 0 }}>{r.geography}</span>
                          <span style={{ flex: 1, fontSize: 12, color: 'var(--text-primary)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {geoName(r.geography)}
                          </span>
                          <div style={{ width: 80, height: 6, background: 'var(--bg-base)', borderRadius: 3, flexShrink: 0 }}>
                            <div style={{ width: `${pct}%`, height: '100%', background: COLORS.teal, borderRadius: 3 }} />
                          </div>
                          <span style={{ width: 64, fontSize: 11, textAlign: 'right', color: 'var(--text-secondary)', flexShrink: 0 }}>
                            {fmtNum(r.active_users)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </ChartCard>
            )}

            {/* Drill-down: shown when a postcode is selected */}
            {hasDrillDown && (
              <>
                {/* Postcode header */}
                <div className="card" style={{ padding: '14px 18px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{
                      width: 44, height: 44, borderRadius: 10, flexShrink: 0,
                      background: 'linear-gradient(135deg, #1DB89A 0%, #0F6E56 100%)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: '#fff', fontWeight: 800, fontSize: 14,
                    }}>
                      {selectedPostcode}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 16 }}>{geoName(selectedPostcode)}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
                        {selectedPostcodeRow ? `${fmtNum(selectedPostcodeRow.active_users)} active users` : '—'} · {effectiveCat} · {fmtMonth(month)}
                      </div>
                    </div>
                    <button
                      onClick={() => setSelectedPostcode(null)}
                      style={{ fontSize: 12, color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px' }}
                    >
                      ← Back to list
                    </button>
                  </div>
                </div>

                {/* App market share — stacked bar (normalized %) */}
                <ChartCard
                  title="App Market Share"
                  sub={`${geoName(selectedPostcode)} · last 6 months · normalised to 100%`}
                >
                  {appsData.loading ? <ChartLoader height={260} /> :
                   appsData.error   ? <ChartError message={appsData.error} height={260} /> :
                   appCategories.length === 0 ? (
                     <div className="empty-state" style={{ height: 260 }}>No app data</div>
                   ) : (
                     <StackedBar
                       categories={appCategories}
                       series={appSeries}
                       height={260}
                       normalize
                     />
                   )}
                </ChartCard>

                {/* Share of category population — line chart */}
                <ChartCard
                  title="Share of Category Population"
                  sub={`${geoName(selectedPostcode)} — last 6 months`}
                >
                  {trendData.loading ? <ChartLoader height={220} /> :
                   trendData.error   ? <ChartError message={trendData.error} height={220} /> :
                   trendSeries.length === 0 ? (
                     <div className="empty-state" style={{ height: 220 }}>No trend data</div>
                   ) : (
                     <LineChart
                       categories={trendMonths}
                       series={trendSeries}
                       height={220}
                       pointLabels={v => v != null ? `${v}%` : ''}
                     />
                   )}
                </ChartCard>
              </>
            )}
          </div>
        )}
      </div>

      {!selectedRegion && !hasDrillDown && !snapshot.loading && regionData.length > 0 && (
        <div style={{ textAlign: 'center', marginTop: 8, color: 'var(--text-secondary)', fontSize: 12 }}>
          {postcodesByUsers.length} postcode areas · click a region on the map to explore
        </div>
      )}
    </div>
  );
}
